import { WORKFLOW_CANVAS as C } from "@sagot/constants";
import { exitSlotsOf, type ExitKey, type ExitSlot } from "../exit-slots";
import type { PathDef } from "../types";

export interface Point {
  x: number;
  y: number;
}
export interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** One row of a path tile, from the top: `y` is measured from the top of the tile. */
export type TileRow =
  | { kind: "step"; y: number; height: number; step: number }
  | { kind: "exit"; y: number; height: number; step: number; slot: ExitSlot }
  | { kind: "empty"; y: number; height: number }
  | { kind: "footer"; y: number; height: number };

/**
 * The rows of a path tile after its header: each step, directly under it one row per exit of that step (a step that
 * never leaves its path has none), an "empty" row while there are no steps, and the "Add step" footer when editable.
 */
export function tileRows(path: PathDef, editable: boolean): TileRow[] {
  const rows: TileRow[] = [];
  let y = C.headerHeight + C.tilePadding;
  if (path.steps.length === 0) {
    rows.push({ kind: "empty", y, height: C.stepHeight });
    y += C.stepHeight;
  }
  path.steps.forEach((step, index) => {
    if (index > 0) y += C.stepGap;
    rows.push({ kind: "step", y, height: C.stepHeight, step: index });
    y += C.stepHeight;
    for (const slot of exitSlotsOf(step)) {
      rows.push({ kind: "exit", y, height: C.exitHeight, step: index, slot });
      y += C.exitHeight;
    }
  });
  y += C.tilePadding;
  if (editable) rows.push({ kind: "footer", y, height: C.footerHeight });
  return rows;
}

/** The height of a path tile: its header and rows, and the padding under the steps. */
export function tileHeight(path: PathDef, editable: boolean): number {
  const last = tileRows(path, editable).at(-1);
  if (!last) return C.headerHeight + C.tilePadding * 2;
  return last.kind === "footer" ? last.y + last.height : last.y + last.height + C.tilePadding;
}

/** Where an exit's connector leaves its tile: the handle on the right border, level with the middle of the exit's row. */
export function handlePoint(box: Box, rows: readonly TileRow[], step: number, key: ExitKey): Point | null {
  const row = rows.find((candidate) => candidate.kind === "exit" && candidate.step === step && candidate.slot.key === key);
  return row ? { x: box.x + box.width, y: box.y + row.y + row.height / 2 } : null;
}

/** Where the trigger's line leaves its tile: the middle of the right edge. */
export const triggerPoint = (box: Box): Point => ({ x: box.x + box.width, y: box.y + box.height / 2 });

/** A point on screen as a point of the canvas: `origin` is the top left of the scrolling viewport's content box on screen. */
export function clientToCanvas(client: Point, origin: Point, scroll: Point, zoom: number): Point {
  return { x: (client.x - origin.x + scroll.x) / zoom, y: (client.y - origin.y + scroll.y) / zoom };
}

/** The tile under a point (the one drawn on top when tiles overlap), or `null` over empty canvas. */
export function tileAt(point: Point, boxes: Readonly<Record<string, Box>>): string | null {
  let found: string | null = null;
  for (const [id, box] of Object.entries(boxes)) {
    if (point.x >= box.x && point.x <= box.x + box.width && point.y >= box.y && point.y <= box.y + box.height) found = id;
  }
  return found;
}

export interface ViewportRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/**
 * How far the canvas should scroll this frame while a connector is dragged: nothing in the middle of the viewport, and
 * from 0 up to `autoPanMaxStep` px the deeper the pointer sits within `autoPanEdge` px of an edge (or beyond it).
 */
export function autoPanDelta(pointer: Point, rect: ViewportRect): Point {
  return { x: panAlong(pointer.x, rect.left, rect.right), y: panAlong(pointer.y, rect.top, rect.bottom) };
}

const panSpeed = (depth: number): number => Math.round(Math.min(Math.max(depth, 0), 1) * C.autoPanMaxStep);

function panAlong(value: number, min: number, max: number): number {
  if (value < min + C.autoPanEdge) return -panSpeed((min + C.autoPanEdge - value) / C.autoPanEdge);
  if (value > max - C.autoPanEdge) return panSpeed((value - (max - C.autoPanEdge)) / C.autoPanEdge);
  return 0;
}

/** The pointer has moved far enough from where it went down to be a drag and not a click. */
export const isDrag = (from: Point, to: Point): boolean => Math.hypot(to.x - from.x, to.y - from.y) >= C.dragThreshold;
